<template>
  <!-- Tenant Card -->
  <BlockContainer class="p-4">
    <!-- v-slot="{ invalid, dirty }" -->
    <ValidationObserver ref="tenancyTenantForm" v-slot="{ invalid, dirty }">
      <div class="row align-items-center">
        <div class="col">
          <h5>Leietaker</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              :disabled="!dirty || invalid || loading.post"
              :loading="loading.post"
              @click.native="saveAndExitEditMode"
              class="ml-1"
            />
          </div>

          <EditButton
            v-else-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo"
            @click.native="setEditModeAndLocalCopy"
            :class="{ sudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo }"
          />
        </div>
      </div>

      <div v-if="getTenancy">
        <div v-if="editMode" class="row">
          <div class="col-12 col-sm-6">
            <ValidationProvider>
              <hl-SwitchWithLabels
                class="mt-4"
                showState
                onState="Privatperson"
                offState="Virksomhet"
                :value="isTenantPrivate"
                @input.native="toggleLocalTenantType"
              ></hl-SwitchWithLabels>
            </ValidationProvider>
          </div>
        </div>

        <!-- Tenant Private -->
        <div v-if="isTenantPrivate" key="private">
          <div class="row mt-4">
            <div class="col">
              <div v-if="editMode">
                <ValidationProvider
                  name="Leietakers navn"
                  :rules="validationRules.tenancy.newTenancyForm.tenant.fullName"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    name="fullName"
                    type="text"
                    label="Leietakers navn"
                    v-model="localCopy.fullName"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>

              <div v-else>
                <p class="m-0">Leietakers navn</p>
                <h6>{{ mainTenant.fullName }}</h6>
              </div>
            </div>

            <div class="col">
              <div v-if="editMode">
                <ValidationProvider
                  name="Fødselsdato"
                  :rules="validationRules.tenancy.newTenancyForm.tenant.dateOfBirth"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    v-model="dob"
                    name="dateOfBirth"
                    type="text"
                    v-mask="'##.##.####'"
                    label="Fødselsdato"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>
              <div v-else>
                <p class="m-0">Fødselsdato</p>
                <h6>
                  <hl-Date v-if="mainTenant.dateOfBirth" :date="mainTenant.dateOfBirth"></hl-Date>
                </h6>
              </div>
            </div>
          </div>
        </div>
        <!-- / Tenant Private --->

        <!-- Tenant Company -->
        <div v-else key="company">
          <div class="row mt-4">
            <div class="col">
              <div v-if="editMode">
                <ValidationProvider name="Virksomhet" v-slot="{ errors }">
                  <hl-SearchCompanies
                    :companyName="localCopy.organizationName"
                    @companySet="populateCompany"
                  ></hl-SearchCompanies>
                  <hl-ErrorMessage v-if="errors[0]">{{ errors[0] }}</hl-ErrorMessage>
                </ValidationProvider>
              </div>

              <div v-else>
                <p class="m-0">{{ translationCompanyName }}</p>
                <h6>{{ mainTenant.organizationName ? mainTenant.organizationName : '' }}</h6>
              </div>
            </div>

            <div class="col">
              <div v-if="editMode">
                <ValidationProvider name="Organisasjonsnummer" rules="required" v-slot="{ errors }">
                  <hl-Input
                    type="text"
                    name="organizationNumber"
                    label="Organisasjonsnummer"
                    v-model="localCopy.organizationNumber"
                    :error="errors[0]"
                    disabled
                  />
                </ValidationProvider>
              </div>
              <div v-else>
                <p class="m-0">Organisasjonsnummer</p>
                <h6>{{ mainTenant.organizationNumber ? mainTenant.organizationNumber : '' }}</h6>
              </div>
            </div>
          </div>

          <div class="mt-4">
            <div v-if="editMode">
              <ValidationProvider name="Kontaktperson" :rules="'required'" v-slot="{ errors }">
                <hl-Input
                  name="contactPerson"
                  label="Kontaktperson"
                  v-model="localCopy.fullName"
                  :error="errors[0]"
                ></hl-Input>
              </ValidationProvider>
            </div>
            <div v-else>
              <p class="m-0">Kontaktperson</p>
              <h6>{{ mainTenant.fullName }}</h6>
            </div>
          </div>
        </div>
        <!-- / Tenant Company -->

        <div class="row mt-3">
          <div class="col">
            <div v-if="editMode">
              <hl-Phone
                type="text"
                label="Landkode"
                placeholder="+47"
                v-model="localCopy.phonePrefix"
                :disabled="localCopy.phonePrefix === '47'"
              >
                <ValidationProvider
                  name="Telefon"
                  :rules="validationRules.userProfile.userInfo.cellPhone"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    type="tel"
                    name="cellPhone"
                    label="Telefon"
                    :placeholder="'phoneExample'"
                    v-model="localCopy.cellPhone"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </hl-Phone>
            </div>

            <div v-else>
              <p class="m-0">Telefon</p>
              <h6 v-if="parsedPhoneNumber">
                {{ parsedPhoneNumber.countryCallingCode ? `+${parsedPhoneNumber.countryCallingCode}` : '' }}
                {{ parsedPhoneNumber.nationalNumber }}
              </h6>
              <h6 v-else>n/a</h6>
            </div>
          </div>

          <div class="col">
            <div v-if="editMode">
              <ValidationProvider name="E-post" :rules="'required|email'" v-slot="{ errors }">
                <hl-Input
                  type="email"
                  name="email"
                  label="E-post"
                  v-model="localCopy.email"
                  :error="errors[0]"
                ></hl-Input>
              </ValidationProvider>
            </div>
            <div v-else>
              <p class="m-0">E-post</p>
              <h6>{{ mainTenant.email }}</h6>
            </div>
          </div>
        </div>

        <div v-if="!editMode" class="mt-3">
          <p class="m-0">Husleie Id</p>
          <h6 v-if="getTenancy.details.husleieId">{{ getTenancy.details.husleieId }}</h6>
        </div>

        <div v-if="address" class="mt-5">
          <h6>Valgfri info for fakturering og andre formål</h6>
          <div class="mt-4">
            <div v-if="editMode">
              <ValidationProvider>
                <hl-Input type="text" name="address-street" label="Adresse" v-model="localAddress.street" />
              </ValidationProvider>
            </div>
            <div v-else class="mt-4">
              <p class="m-0">Adresse</p>
              <h6>{{ address.street ? address.street : '' }}</h6>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col">
              <div v-if="editMode">
                <ValidationProvider>
                  <hl-Input
                    type="number"
                    name="postalZipcode"
                    label="Postnummer"
                    v-model="localAddress.postalZipcode"
                    @input="fetchPostalArea"
                  />
                </ValidationProvider>
              </div>
              <div v-else>
                <p class="m-0">Postnummer</p>
                <h6>{{ address.postalZipcode }}</h6>
              </div>
            </div>

            <div class="col">
              <div v-if="editMode">
                <ValidationProvider>
                  <hl-Input type="text" name="postalArea" label="Poststed" v-model="localAddress.postalArea" disabled />
                </ValidationProvider>
              </div>
              <div v-else>
                <p class="m-0">Poststed</p>
                <h6>{{ address.postalArea }}</h6>
              </div>
            </div>
          </div>
        </div>

        <TenancyCoTenants :landlordId="landlordId" :tenancyId="tenancyId" class="mt-5" />
      </div>
    </ValidationObserver>
  </BlockContainer>
  <!-- / Tenant Card -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import addValidationRules from '@/mixins/addValidationRules.js';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Phone from '@modules/husleie-framework/src/components/forms/inputs/phone/Phone.vue';
import SearchCompanies from '@modules/husleie-framework/src/components/forms/inputs/search/SearchCompanies.vue';
import SwitchWithLabels from '@modules/husleie-framework/src/components/forms/inputs/switch/SwitchWithLabels.vue';
import ErrorMessage from '@modules/husleie-framework/src/components/text/ErrorMessage.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import TenancyCoTenants from '@/components/tenancy/tenancy/TenancyCoTenants';
import i18nCommon from '@modules/i18n/mixins/Common';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import { mask } from 'vue-the-mask';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    TenancyCoTenants,
    'hl-Input': Input,
    'hl-Phone': Phone,
    'hl-ErrorMessage': ErrorMessage,
    'hl-SearchCompanies': SearchCompanies,
    'hl-SwitchWithLabels': SwitchWithLabels,
    'hl-Date': Date,
  },

  props: ['landlordId', 'tenancyId'],

  mixins: [addValidationRules, i18nCommon],
  directives: { mask },

  data() {
    return {
      editMode: false,
      isTenantPrivate: true,
      loading: {
        get: false,
        post: false,
      },
      localCopy: {},
      dob: '',
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancy']),
    ...mapGetters(['isUserSudo']),

    mainTenant() {
      const tenants = this.getTenancy.tenants;
      if (tenants == null) return;
      const mainTenant = tenants.find(tenant => tenant.type === 'tenant');
      if (mainTenant == null) return;
      return mainTenant;
    },

    address() {
      if (this.mainTenant == null) return;
      return this.mainTenant.address;
    },

    localAddress() {
      if (this.localCopy == null || this.localCopy.address == null) return;
      return this.localCopy.address;
    },

    parsedPhoneNumber() {
      if (this.mainTenant == null || this.mainTenant.cellPhone == null) return;
      let parsedPhoneNumber = this.mainTenant.cellPhone;
      // Norwegian phones
      if (parsedPhoneNumber.length === 8 && (parsedPhoneNumber[0] == 4 || parsedPhoneNumber[0] == 9)) {
        parsedPhoneNumber = parsePhoneNumberFromString(`+47${this.mainTenant.cellPhone}`);
      }
      // foreign phones
      else {
        // parsing phones that already start with +
        if (this.mainTenant.cellPhone[0] === '+') {
          parsedPhoneNumber = parsePhoneNumberFromString(`${this.mainTenant.cellPhone}`);
        }
        // parsing phones without a starting +
        else {
          parsedPhoneNumber = parsePhoneNumberFromString(`+${this.mainTenant.cellPhone}`);
        }
      }
      return parsedPhoneNumber;
    },
    translationCompanyName() {
      return this.translate('company name');
    },
  },

  methods: {
    ...mapActions('tenancy', ['updateTenancyTenant']),

    toggleLocalTenantType() {
      this.isTenantPrivate = !this.isTenantPrivate;
      this.$nextTick(() => {
        this.$refs.tenancyTenantForm.validate();
      });
    },

    populateCompany(c) {
      this.$set(this.localCopy, 'organizationName', c.navn);
      this.$set(this.localCopy, 'organizationNumber', c.organisasjonsnummer);
    },

    setEditModeAndLocalCopy() {
      if (this.mainTenant == null) return;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.mainTenant)));
      this.dob = moment(this.localCopy.dateOfBirth).format('DD.MM.YYYY');
      if (this.mainTenant.cellPhone) {
        console.log('aqui', this.mainTenant.cellPhone);
        let parsedPhoneNumber = this.mainTenant.cellPhone;

        if (parsedPhoneNumber.length === 8 && (parsedPhoneNumber[0] == 4 || parsedPhoneNumber[0] == 9)) {
          parsedPhoneNumber = parsePhoneNumberFromString(`+47${this.mainTenant.cellPhone}`);
        } else {
          if (this.mainTenant.cellPhone[0] === '+') {
            parsedPhoneNumber = parsePhoneNumberFromString(`${this.mainTenant.cellPhone}`);
          } else {
            parsedPhoneNumber = parsePhoneNumberFromString(`+${this.mainTenant.cellPhone}`);
          }
        }
        if (parsedPhoneNumber) {
          this.localCopy.phonePrefix = parsedPhoneNumber.countryCallingCode;
          this.localCopy.cellPhone = parsedPhoneNumber.nationalNumber;
        }
      }

      this.editMode = true;
      //this.localCopy = this.mainTenant;
    },

    async saveAndExitEditMode() {
      this.loading.post = true;
      try {
        await this.updateTenancyTenant({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          tenantId: this.localCopy.id,
          data: this.localCopy,
        });
        this.loading.post = false;
        this.editMode = false;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre leietaker.', 'error');
      }
    },

    fetchPostalArea(e) {
      const axios = require('axios');
      const that = this;

      // Make a request for a user with a given ID
      if (e.match(/^\d\d\d\d$/)) {
        axios
          .get('/api/poststed/' + e)
          .then(function (response) {
            // handle success
            console.log('response.data.poststed', response.data.poststed);
            that.localAddress.postalArea = response.data.poststed;
          })
          .catch(function (error) {
            // handle error
            console.error(error);
          });
      }
    },
  },

  watch: {
    isTenantPrivate(newVal) {
      if (newVal) {
        // we remove the company info when we switch back to private
        this.$delete(this.localCopy, 'organizationName');
        this.$delete(this.localCopy, 'organizationNumber');
      }
    },

    'localAddress.postalZipcode'(newVal) {
      if (newVal === '') {
        this.localAddress.postalArea = '';
      }
    },

    dob(v) {
      const parsed = moment(v, 'DD.MM.YYYY', true);
      if (parsed.isValid()) {
        this.localCopy.dateOfBirth = parsed.format('YYYY-MM-DD');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.custom-tabs {
  border-top: 1px solid $gray-300;
  border-bottom: 1px solid $gray-300;
  padding: 3px 1.5rem 0 1.5rem;
  box-shadow: $box-shadow-2;

  .nav-tabs {
    border-bottom: 0;
    font-weight: 400;
  }
  .nav-link {
    padding: 1rem 2rem;
    color: $secondary;
  }

  .active {
    border: none;
    border-bottom: 3px solid $primary;
    color: $primary;
  }
}

.edit-buttons {
  flex: 0 0 200px;
}

.sudo {
  border: 2px solid #ed2135;
}
</style>
